import { useState } from "react";
import { Disc3 } from "lucide-react";
import { useApi, usePagedList } from "@/lib/api";
import { EventCard } from "@/components/site/EventCard";
import { ClubWeekend } from "@/components/site/ClubWeekend";
import { Container, LoadingGrid, PageHeader } from "@/components/site/PageHeader";
import { LoadMore } from "@/components/site/ListControls";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const tabCls =
  "rounded-none font-display uppercase text-lg px-6 py-3 text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-black";

export default function Agenda() {
  const [when, setWhen] = useState("upcoming");
  const [city, setCity] = useState("all");
  const [club, setClub] = useState(false);
  const { data: cities = [] } = useApi("/events-cities");
  const list = usePagedList("/events", { when, city: city === "all" ? undefined : city }, 24);
  const data = list.items;
  const isLoading = list.isLoading;

  return (
    <>
      <PageHeader
        top="Agenda"
        bottom="concerts"
        intro="Concerts, cartes blanches, battles, showcases et émissions live : les rendez-vous LGTDZ en France et en Europe."
        testId="agenda-header"
      />
      <Container className="py-12 flex flex-col md:flex-row gap-6 md:items-center justify-between border-b border-white/10">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
          <Tabs value={club ? "" : when} onValueChange={(v) => { setClub(false); setWhen(v); }}>
            <TabsList className="rounded-none bg-transparent border border-white/20 p-0 h-auto">
              <TabsTrigger value="upcoming" className={tabCls} data-testid="agenda-tab-upcoming">À venir</TabsTrigger>
              <TabsTrigger value="past" className={tabCls} data-testid="agenda-tab-past">Passés</TabsTrigger>
              <TabsTrigger value="all" className={tabCls} data-testid="agenda-tab-all">Tous</TabsTrigger>
            </TabsList>
          </Tabs>
          <button type="button" onClick={() => setClub((v) => !v)} aria-pressed={club} data-testid="agenda-club-weekend-btn"
            className={`inline-flex items-center justify-center gap-2 h-[52px] px-5 border font-display uppercase text-base transition-colors duration-200 ${club ? "bg-primary border-primary text-white" : "border-white/20 text-white hover:border-primary hover:text-primary"}`}>
            <Disc3 className={`w-5 h-5 ${club ? "animate-spin" : ""}`} /> Ce week-end en club
          </button>
        </div>
        {!club && <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="w-full md:w-64 rounded-none border-white/20 bg-transparent h-12 uppercase tracking-[0.15em] text-xs font-bold" data-testid="agenda-city-select">
            <SelectValue placeholder="Ville" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-black border-white/10">
            <SelectItem value="all" data-testid="agenda-city-all">Toutes les villes</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c} value={c} data-testid={`agenda-city-${c}`}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>}
      </Container>
      <Container className="py-20">
        {club ? <ClubWeekend /> : isLoading ? (
          <LoadingGrid />
        ) : data.length === 0 ? (
          <div className="py-20" data-testid="agenda-empty">
            <p className="font-display uppercase text-5xl md:text-7xl text-white">Rien de prévu<span className="text-primary">.</span></p>
            <p className="text-zinc-400 mt-4 max-w-lg">
              Pas de date annoncée pour l'instant. Inscris-toi à la newsletter pour être prévenu·e des prochains concerts, ou consulte les événements passés.
            </p>
            <button onClick={() => setWhen("past")} data-testid="agenda-see-past-btn" className="mt-8 border-2 border-white text-white font-display uppercase text-xl px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">
              Voir les événements passés
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16" data-testid="agenda-grid">
            {data.map((e, i) => <EventCard key={e.id} event={e} index={i % 24} />)}
          </div>
        )}
        {!club && <LoadMore list={list} testId="agenda-load-more" />}
      </Container>
    </>
  );
}
